// =====================================================================
// src/lib/frigobar.js — Loja e Frigobar, sem DOM
//   (MdM `statusEstoque` 10702 · `renderLoja` 12978 (hub, loja, flats) ·
//    `_eefRecalcDiff`/`_eefAplicar` 11060 · `salvarFrigobarItens` 16206 ·
//    `renderGestaoLoja` 13124 · `_calcFrigobarData` (src/lib/calc/
//    frigobar.js) · `resultado-holding.js` 122–156)
//
// O que o MdM guarda em `produtos`, `estoque`, `estoque_loja`,
// `estoque_padrao` e `movimentos` aqui são as tabelas da 0056: `produtos`
// (com `estoque_padrao`), `estoque_da_unidade`, `estoque_loja`,
// `movimentos_de_frigobar`. Linha de estoque ausente É zero (o MdM
// pré-cria linhas com qty=0; ver a 0056).
//
// Divergências de domínio: o frigobar é do DONO da unidade (decisão 3 do
// Hugo, 22/09/2026) — o checklist do dia mora aqui, não na ficha da
// governança; o custo de TODA venda espelha (o MdM só espelhava o do
// checklist); a comissão é % por imobiliária (2A), não 5% fixo.
// =====================================================================
import { MESES } from './financeiro.js';

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const int = v => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : 0; };

export const CATEGORIAS = Object.freeze([
  { chave: 'bebida', rotulo: 'Bebida' }, { chave: 'alimento', rotulo: 'Alimento' }, { chave: 'outro', rotulo: 'Outro' },
]);
export const TIPOS_DA_LOJA = Object.freeze(['Aluguel', 'Luz', 'Água', 'Condomínio Loja', 'Internet']);   // `_lojaCats` 12850
export const MOTIVOS = Object.freeze([
  { chave: 'venda', rotulo: '💰 Venda (gera receita)' }, { chave: 'perda', rotulo: '🗑️ Perda/avaria' }, { chave: 'ajuste', rotulo: '📋 Ajuste de inventário' },
]);
export const TIPOS_DE_MOVIMENTO = Object.freeze({
  checklist: 'Frigobar do dia', consumo: 'Lançamento manual', ajuste: 'Ajuste de saldos', entrada_loja: 'Entrada na loja', ajuste_loja: 'Inventário da loja',
});

// ── saldos ───────────────────────────────────────────────────────────
export const saldoDaUnidade = (estoque, unidadeId, produtoId) => int(((estoque || []).find(e => e.unidade_id === unidadeId && e.produto_id === produtoId) || {}).quantidade);
export const saldoDaLoja = (loja, produtoId) => int(((loja || []).find(e => e.produto_id === produtoId) || {}).quantidade);
export const lucroUnitario = p => num(p.preco) - num(p.valor_compra);

// `statusEstoque` (10702): o padrão do produto define a sinalização
export function statusEstoque(qty, padrao) {
  qty = int(qty); padrao = int(padrao);
  if (padrao > 0 && qty === 0) return { cor: '#dc2626', emoji: '🔴', rotulo: 'Zerado', critico: true, parcial: false };
  if (padrao > 0 && qty < padrao) return { cor: '#d97706', emoji: '🟡', rotulo: 'Parcial', critico: false, parcial: true };
  return { cor: '#059669', emoji: '🟢', rotulo: 'Completo', critico: false, parcial: false };
}
// a loja (13060): 🟢 > 5 · 🟡 ≤ 5 · 🔴 ≤ 2
export function statusLoja(qty) {
  qty = int(qty);
  if (qty <= 2) return { cor: '#dc2626', emoji: '🔴', rotulo: 'Crítico' };
  if (qty <= 5) return { cor: '#d97706', emoji: '🟡', rotulo: 'Baixo' };
  return { cor: '#059669', emoji: '🟢', rotulo: 'OK' };
}
// o cartão de cada flat na grade (13100): quantos produtos críticos/parciais
export function situacaoDaUnidade(produtos, estoque, unidadeId) {
  let critico = 0, parcial = 0;
  for (const p of (produtos || []).filter(x => x.ativo !== false)) {
    const s = statusEstoque(saldoDaUnidade(estoque, unidadeId, p.id), p.estoque_padrao);
    if (s.critico) critico++; else if (s.parcial) parcial++;
  }
  const cor = critico > 0 ? '#dc2626' : parcial > 0 ? '#d97706' : '#059669';
  const texto = critico > 0 ? `${critico} crítico${critico > 1 ? 's' : ''}` : parcial > 0 ? `${parcial} parcial${parcial > 1 ? 'is' : ''}` : 'Completo';
  return { critico, parcial, cor, texto, borda: critico > 0 ? '2px solid #dc2626' : parcial > 0 ? '2px solid #d97706' : '2px solid #e5e7eb' };
}
// os números dos dois cards do hub (13000)
export function resumoDoHub(produtos, loja, estoque, unidades) {
  const ativos = (produtos || []).filter(p => p.ativo !== false);
  const qtdLoja = ativos.reduce((s, p) => s + saldoDaLoja(loja, p.id), 0);
  const lojaCriticos = ativos.filter(p => saldoDaLoja(loja, p.id) <= 2).length;
  const flatsCriticos = (unidades || []).filter(u => situacaoDaUnidade(ativos, estoque, u.id).critico > 0).length;
  return { produtos: ativos.length, qtdLoja, lojaCriticos, flats: (unidades || []).length, flatsCriticos };
}
export const ordenarPorNome = lista => [...(lista || [])].sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR'));
export const ordenarPorNumero = unidades => [...(unidades || [])].sort((a, b) => (parseInt(a.numero, 10) || 0) - (parseInt(b.numero, 10) || 0) || String(a.numero).localeCompare(String(b.numero)));

// ── o ajuste em massa (`_eefRecalcDiff`) ──────────────────────────────
// o que a tela manda para `ajustar_estoque_da_unidade`: só o que mudou
export function mudancasDoAjuste(linhas) {
  return (linhas || []).map(l => ({ produto_id: l.produto_id, atual: int(l.atual), novo: int(l.novo), motivo: l.motivo || null }))
    .filter(l => l.novo >= 0 && l.novo !== l.atual)
    .map(l => ({ produto_id: l.produto_id, novo: l.novo, ...(l.novo < l.atual ? { motivo: l.motivo || 'ajuste' } : {}) }));
}
// a Loja tem saldo para as reposições? (a pergunta antes de aplicar, 11097)
export function deficitsDaLoja(mudancas, produtos, loja, estoque, unidadeId) {
  const out = [];
  for (const m of mudancas || []) {
    const delta = m.novo - saldoDaUnidade(estoque, unidadeId, m.produto_id);
    if (delta <= 0) continue;
    const tem = saldoDaLoja(loja, m.produto_id);
    if (tem < delta) out.push({ nome: (((produtos || []).find(p => p.id === m.produto_id)) || {}).nome || '?', precisa: delta, tem });
  }
  return out;
}

// ── o checklist do dia (`salvarFrigobarItens`) ───────────────────────
// o que a tela manda para `salvar_frigobar_do_dia`: a lista inteira do dia
export function itensDoChecklist(linhas) {
  return (linhas || []).map(l => ({ produto_id: l.produto_id, vendidos: Math.max(0, int(l.vendidos)), repostos: Math.max(0, int(l.repostos)) }))
    .filter(l => l.vendidos > 0 || l.repostos > 0);
}
export function totalDoChecklist(itens, produtos) {
  let venda = 0, custo = 0;
  for (const it of itens || []) { const p = (produtos || []).find(x => x.id === it.produto_id); if (!p) continue; venda += it.vendidos * num(p.preco); custo += it.vendidos * num(p.valor_compra); }
  return { venda, custo };
}
// o checklist já salvo de uma unidade num dia
export function checklistDoDia(movimentos, unidadeId, dia) {
  return (movimentos || []).find(m => m.tipo === 'checklist' && m.unidade_id === unidadeId && m.data === dia) || null;
}

// ── o relatório (`_calcFrigobarData`) ────────────────────────────────
// venda, custo e lucro do período, por produto e por unidade. O CUSTO sai
// do valor de compra ATUAL do produto (regra do Hugo, 07/09/2026), não do
// da época. Entram as vendas de todo movimento (checklist, manual, ajuste
// por venda) — ver a divergência no cabeçalho.
export function dadosDoFrigobar(produtos, movimentos, unidades, de, ate) {
  const dentro = d => d && (!de || d >= de) && (!ate || d <= ate);
  const prodPor = new Map((produtos || []).map(p => [p.id, p]));
  const uniPor = new Map((unidades || []).map(u => [u.id, u]));
  const porItem = new Map(), porUnidade = new Map();
  for (const m of (movimentos || []).filter(x => ['checklist', 'consumo', 'ajuste'].includes(x.tipo) && dentro(x.data))) {
    for (const [pid, it] of Object.entries(m.itens || {})) {
      const qtd = int(it.vendidos); if (qtd <= 0) continue;
      const prVenda = num(it.preco), prCompra = num((prodPor.get(pid) || {}).valor_compra);
      if (!porItem.has(pid)) porItem.set(pid, { produto_id: pid, nome: (prodPor.get(pid) || {}).nome || it.nome || '?', vend: 0, receita: 0, custo: 0 });
      const i = porItem.get(pid); i.vend += qtd; i.receita += qtd * prVenda; i.custo += qtd * prCompra;
      const u = uniPor.get(m.unidade_id);
      if (!porUnidade.has(m.unidade_id)) porUnidade.set(m.unidade_id, { unidade_id: m.unidade_id, numero: u ? u.numero : '?', venda: 0, custo: 0 });
      const f = porUnidade.get(m.unidade_id); f.venda += qtd * prVenda; f.custo += qtd * prCompra;
    }
  }
  const itens = [...porItem.values()].sort((a, b) => b.receita - a.receita);
  const flats = [...porUnidade.values()].sort((a, b) => (parseInt(a.numero, 10) || 0) - (parseInt(b.numero, 10) || 0));
  const totRec = itens.reduce((s, x) => s + x.receita, 0), totCus = itens.reduce((s, x) => s + x.custo, 0);
  return { porItem: itens, porUnidade: flats, totRec, totCus, totLuc: totRec - totCus };
}
// o que o Resultado da Holding soma (resultado-holding.js 155): receita − custo − comissão%
export function lucroDoFrigobar(receita, custo, comissaoPct) {
  const comissao = num(receita) * num(comissaoPct) / 100;
  return { receita: num(receita), custo: num(custo), comissao, comissaoPct: num(comissaoPct), lucro: num(receita) - num(custo) - comissao };
}

// ── a Gestão da Loja (`renderGestaoLoja`): lançamentos `loja`, "Tipo | Nota" ─
export const mesDe = iso => (iso || '').slice(0, 7);
export function rotuloDoMes(ym) { if (!ym) return ''; const [a, m] = ym.split('-'); return `${MESES[Number(m) - 1]} ${a}`; }
export function mesesDisponiveis(lancamentos, hoje) {
  const anoHoje = Number(hoje.slice(0, 4));
  let anoIni = anoHoje;
  for (const l of lancamentos || []) if (l.data) anoIni = Math.min(anoIni, Number(l.data.slice(0, 4)));
  const lista = [];
  for (let a = anoHoje + 1; a >= anoIni; a--) for (let m = 12; m >= 1; m--) lista.push(`${a}-${String(m).padStart(2, '0')}`);
  return lista;
}
export function partirDescricao(descricao) {
  const p = String(descricao || '').split('|');
  return { tipo: p[0].trim(), nota: p.slice(1).join('|').trim() };
}
export const montarDescricao = (tipo, nota) => nota && nota.trim() ? `${tipo} | ${nota.trim()}` : tipo;
export function despesasDaLoja(lancamentos, mes) {
  return (lancamentos || []).filter(l => l.categoria === 'loja' && l.tipo === 'despesa' && l.data && l.data.startsWith(mes))
    .sort((a, b) => a.data.localeCompare(b.data));
}
export function resumoDaLoja(lancs, tipos) {
  const porTipo = new Map(tiposParaTela(tipos).map(t => [t, 0]));
  let total = 0;
  for (const l of lancs) { const { tipo } = partirDescricao(l.descricao); const v = num(l.valor); total += v; porTipo.set(tipo, (porTipo.get(tipo) || 0) + v); }
  return { porTipo, total };
}
export function tiposParaTela(tipos) { return (tipos && tipos.length) ? tipos.map(t => t.nome) : [...TIPOS_DA_LOJA]; }
// mês vazio com o anterior preenchido → oferece copiar (`_ofertarCopiaLoja` 12706)
export function mesDeOrigem(lancamentos, mesDestino) {
  if (despesasDaLoja(lancamentos, mesDestino).length) return null;
  let [a, m] = mesDestino.split('-').map(Number);
  for (let i = 0; i < 12; i++) {
    m -= 1; if (m < 1) { m = 12; a -= 1; }
    const ym = `${a}-${String(m).padStart(2, '0')}`;
    if (despesasDaLoja(lancamentos, ym).length) return ym;
  }
  return null;
}
// o dia do lançamento de origem, preso ao fim do mês de destino
export function dataNoMes(dataOrigem, mesDestino) {
  const dia = Number(dataOrigem.slice(8, 10)) || 1;
  const [a, m] = mesDestino.split('-').map(Number);
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return `${mesDestino}-${String(Math.min(dia, ultimo)).padStart(2, '0')}`;
}

// ── validação dos formulários ────────────────────────────────────────
export function validarProduto({ nome, categoria, valor_compra, preco }) {
  const erros = [];
  if (!String(nome || '').trim()) erros.push('Informe o nome do produto.');
  if (!CATEGORIAS.some(c => c.chave === categoria)) erros.push('Escolha a categoria.');
  if (num(valor_compra) < 0 || num(preco) < 0) erros.push('Valores não podem ser negativos.');
  return erros;
}
export function validarEntrada({ produto_id, quantidade }) {
  const erros = [];
  if (!produto_id) erros.push('Selecione um produto.');
  if (!(int(quantidade) > 0)) erros.push('Informe a quantidade.');
  return erros;
}
export function validarMovimento({ vendidos, repostos }) {
  if (int(vendidos) < 0 || int(repostos) < 0) return ['Quantidades não podem ser negativas.'];
  return (int(vendidos) > 0 || int(repostos) > 0) ? [] : ['Informe quantidade vendida ou reposta.'];
}
export function validarDespesaDaLoja({ data, tipo, valor }) {
  const erros = [];
  if (!data) erros.push('Informe a data.');
  if (!String(tipo || '').trim()) erros.push('Informe o tipo de despesa.');
  if (!(num(valor) > 0)) erros.push('Informe o valor.');
  return erros;
}
